import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import Typography from '@mui/material/Typography';
import { IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import "devextreme/dist/css/dx.light.css";
import DataGrid, {
  Column,
  Editing,
  Selection,
  RangeRule,
  Sorting,
} from "devextreme-react/data-grid";
import NoticeEmitter from '../../../../NoticeUtil';

const CM_PER_IN = 2.54;
const LB_PER_KG = 2.20462262;
const DIM_FIELDS = ['length', 'width', 'height'];

// Imperial stored value → metric display value
const toMetric = (field, val) => {
  if (val === '' || val === null || val === undefined) return val;
  const n = Number(val);
  if (Number.isNaN(n)) return val;
  if (DIM_FIELDS.includes(field)) return +(n * CM_PER_IN).toFixed(1);
  if (field === 'weight') return +(n / LB_PER_KG).toFixed(2);
  return val;
};

// Metric typed value → imperial for storage
const toImperial = (field, val) => {
  if (val === '' || val === null || val === undefined) return val;
  const n = Number(val);
  if (Number.isNaN(n)) return val;
  if (DIM_FIELDS.includes(field)) return +(n / CM_PER_IN).toFixed(4);
  if (field === 'weight') return +(n * LB_PER_KG).toFixed(4);
  return val;
};

const createEmptyRow = (id) => ({ id, type: '', length: '', width: '', height: '', weight: '', positions: '', quantity: '', description: '' });

const isRowEmpty = (row) => {
    if (!row) return true;
    return !row.type && !row.length && !row.width && !row.height && !row.weight && !row.positions && !row.quantity && !row.description;
}

function DefineIndividualPieces(props) {
  const dataGridRef = useRef(null);
  const { isMetric } = props;

  // Source of truth — always imperial, sent to payload
  const [imperialList, setImperialList] = useState(() => {
    const initialData = props.initialData || [];
    const maxId = initialData.length > 0 ? Math.max(...initialData.map(p => p.id)) : 0;
    return [...initialData, createEmptyRow(maxId + 1)];
  });

  // Grid data source — values in the currently selected unit
  const [displayList, setDisplayList] = useState(() => {
    const initialData = props.initialData || [];
    const maxId = initialData.length > 0 ? Math.max(...initialData.map(p => p.id)) : 0;
    return [...initialData, createEmptyRow(maxId + 1)];
  });

  // Ref so the unit-toggle effect always reads the latest imperial values
  const imperialListRef = useRef(imperialList);
  useEffect(() => { imperialListRef.current = imperialList; }, [imperialList]);

  // Sync a freshly-computed imperial list to the parent immediately (no useEffect
  // delay) so handleNextPanel always reads the latest values even when the user
  // clicks Next right after typing without tabbing away first.
  const syncImperial = (newList) => {
    setImperialList(newList);
    props.setDefineIndividual(newList.filter(row => !isRowEmpty(row)));
  };

  // Unit toggle — recompute display values from the imperial source
  useEffect(() => {
    setDisplayList(
      imperialListRef.current.map(row => ({
        ...row,
        ...(isMetric && {
          length: toMetric('length', row.length),
          width:  toMetric('width',  row.width),
          height: toMetric('height', row.height),
          weight: toMetric('weight', row.weight),
        }),
      }))
    );
  }, [isMetric]);

  // Helper: apply a single-field commit to a list (functional updater shape)
  const applyCommit = (currentList, rowKey, field, value) => {
    const lastRowId = currentList[currentList.length - 1]?.id;
    const wasLastRow = rowKey === lastRowId;
    let newList = currentList.map(item =>
      item.id === rowKey ? { ...item, [field]: value } : item
    );
    if (wasLastRow) {
      const updatedRow = newList.find(item => item.id === rowKey);
      if (updatedRow && !isRowEmpty(updatedRow)) {
        const newId = Math.max(...newList.map(r => r.id)) + 1;
        newList = [...newList, createEmptyRow(newId)];
      }
    }
    return newList;
  };

  const onRowUpdated = (e) => {
    // Compute new imperial list eagerly from the ref so we can sync to the
    // parent right now — before React re-renders and before Next is clicked.
    const imperialData = isMetric
      ? Object.fromEntries(
          Object.entries(e.data).map(([k, v]) =>
            (DIM_FIELDS.includes(k) || k === 'weight') ? [k, toImperial(k, v)] : [k, v]
          )
        )
      : e.data;

    const base = imperialListRef.current;
    const lastRowId = base[base.length - 1].id;
    const wasLastRow = e.key === lastRowId;
    let newImperialList = base.map(item =>
      item.id === e.key ? { ...item, ...imperialData } : item
    );

    // Auto-set quantity to 1 when a dimension or weight is filled but quantity is empty
    const updatedImperialRow = newImperialList.find(item => item.id === e.key);
    const autoQty =
      updatedImperialRow &&
      !updatedImperialRow.quantity &&
      (updatedImperialRow.length || updatedImperialRow.width || updatedImperialRow.height || updatedImperialRow.weight);

    if (autoQty) {
      newImperialList = newImperialList.map(item =>
        item.id === e.key ? { ...item, quantity: 1 } : item
      );
    }

    if (wasLastRow) {
      const finalRow = newImperialList.find(item => item.id === e.key);
      if (finalRow && !isRowEmpty(finalRow)) {
        const newId = Math.max(...newImperialList.map(r => r.id)) + 1;
        newImperialList = [...newImperialList, createEmptyRow(newId)];
      }
    }

    // displayList: store typed values as-is (already in the selected unit)
    setDisplayList(currentList => {
      const lastDisplayRowId = currentList[currentList.length - 1].id;
      const wasLastDisplayRow = e.key === lastDisplayRowId;
      const displayData = autoQty ? { ...e.data, quantity: 1 } : e.data;
      const updatedList = currentList.map(item =>
        item.id === e.key ? { ...item, ...displayData } : item
      );
      if (wasLastDisplayRow) {
        const updatedRow = updatedList.find(item => item.id === e.key);
        if (updatedRow && !isRowEmpty(updatedRow)) {
          const newId = Math.max(...updatedList.map(r => r.id)) + 1;
          return [...updatedList, createEmptyRow(newId)];
        }
      }
      return updatedList;
    });

    syncImperial(newImperialList);
    NoticeEmitter.emit('PackagePiecesUpdate', 'update');
  };

  const onRowRemoved = (id) => {
    setDisplayList(prev => prev.filter(obj => obj.id !== id));
    syncImperial(imperialListRef.current.filter(obj => obj.id !== id));
    setTimeout(() => NoticeEmitter.emit('PackagePiecesUpdate', 'minus'), 500);
  }

  const onGridBlur = (e) => {
    if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    if (dataGridRef.current) {
      dataGridRef.current.instance().saveEditData();
    }
  };

  const renderDelete = params => (
    <IconButton
      color="primary"
      onClick={() => onRowRemoved(params.data.id)}
      disabled={isRowEmpty(params.data) && displayList.filter(r => isRowEmpty(r)).length === 1}
      tabIndex={-1}
    >
      <ClearIcon style={{ fontSize: 12 }}/>
    </IconButton>
  );

  const onCellPrepared = (e) => {
    if (e.rowType === "data" && !e.column?.dataField) {
      e.cellElement.classList.add('dx-cell-focus-disabled');
      e.cellElement.setAttribute('tabindex', '-1');
    }
    if (e.rowType === "data" && typeof e.value === "number" && e.value < 0) {
      e.cellElement.style.setProperty('color', 'red', 'important');
      e.cellElement.style.setProperty('font-weight', 'bold', 'important');
      e.cellElement.style.border = "2px solid red";
    }
  };

  const onFocusedCellChanging = (e) => {
    const cols = e.component?.getVisibleColumns?.();
    if (!cols || e.newColumnIndex == null) return;
    const target = cols[e.newColumnIndex];
    if (target && !target.dataField) {
      e.cancel = true;
    }
  };

  const EDITABLE_FIELDS = ['quantity', 'length', 'width', 'height', 'weight', 'description', 'positions'];

  const focusCell = (instance, rowIdx) => {
    const rowEl = instance.getRowElement(rowIdx)?.[0];
    const input = rowEl?.querySelector('.dx-texteditor-input');
    if (input) {
      input.focus();
      if (typeof input.select === 'function') input.select();
    } else {
      requestAnimationFrame(() => {
        const r = instance.getRowElement(rowIdx)?.[0];
        const i = r?.querySelector('.dx-texteditor-input');
        if (i) { i.focus(); if (typeof i.select === 'function') i.select(); }
      });
    }
  };

  const handleGridKeyDown = (dxEvent) => {
    const e = dxEvent.event;
    if (!e || e.key !== 'Tab') return;

    const instance = dxEvent.component;
    if (!instance) return;

    const isEditingInput = !!e.target?.classList?.contains('dx-texteditor-input');
    const td = isEditingInput
      ? e.target.closest?.('td')
      : e.target?.tagName === 'TD' ? e.target : e.target?.closest?.('td');
    const tr = td?.closest?.('tr.dx-data-row');
    const tbody = tr?.parentElement;
    if (!td || !tr || !tbody) return;

    const visibleColumns = instance.getVisibleColumns();
    const tdIdx = Array.from(tr.children).indexOf(td);
    if (tdIdx < 0 || tdIdx >= visibleColumns.length) return;
    const currentColumn = visibleColumns[tdIdx];
    const currentField = currentColumn?.dataField;
    if (!currentField || !EDITABLE_FIELDS.includes(currentField)) return;

    const editableFields = visibleColumns
      .filter(col => col.dataField && EDITABLE_FIELDS.includes(col.dataField))
      .map(col => col.dataField);

    const currentFieldIdx = editableFields.indexOf(currentField);
    const dataRows = Array.from(tbody.querySelectorAll('tr.dx-data-row'));
    const currentRowIdx = dataRows.indexOf(tr);

    const direction = e.shiftKey ? -1 : 1;
    let nextFieldIdx = currentFieldIdx + direction;
    let nextRowIdx = currentRowIdx;

    if (nextFieldIdx >= editableFields.length) {
      nextFieldIdx = 0;
      nextRowIdx += 1;
    } else if (nextFieldIdx < 0) {
      nextFieldIdx = editableFields.length - 1;
      nextRowIdx -= 1;
    }

    if (nextRowIdx < 0 || nextRowIdx >= instance.getVisibleRows().length) {
      return;
    }

    dxEvent.handled = true;
    e.preventDefault();

    if (isEditingInput) {
      const rawValue = e.target.value;
      let displayValue;
      if (rawValue === '' || rawValue == null) {
        displayValue = '';
      } else if (currentColumn?.dataType === 'number') {
        const parsed = Number(rawValue);
        displayValue = Number.isNaN(parsed) ? '' : parsed;
      } else {
        displayValue = rawValue;
      }

      const imperialValue = isMetric && displayValue !== ''
        ? toImperial(currentField, displayValue)
        : displayValue;

      const rowKey = instance.getKeyByRowIndex(currentRowIdx);
      let newImperialList = applyCommit(imperialListRef.current, rowKey, currentField, imperialValue);

      // Auto-set quantity to 1 when a dimension or weight is filled but quantity is empty
      const tabUpdatedRow = newImperialList.find(item => item.id === rowKey);
      const tabAutoQty =
        tabUpdatedRow &&
        !tabUpdatedRow.quantity &&
        (tabUpdatedRow.length || tabUpdatedRow.width || tabUpdatedRow.height || tabUpdatedRow.weight);

      if (tabAutoQty) {
        newImperialList = newImperialList.map(item =>
          item.id === rowKey ? { ...item, quantity: 1 } : item
        );
      }

      // flushSync keeps both lists in sync before editCell opens the next editor
      flushSync(() => {
        setDisplayList(current => {
          const updated = applyCommit(current, rowKey, currentField, displayValue);
          if (tabAutoQty) {
            return updated.map(item => item.id === rowKey ? { ...item, quantity: 1 } : item);
          }
          return updated;
        });
        syncImperial(newImperialList);
      });
      NoticeEmitter.emit('PackagePiecesUpdate', 'update');
    }

    instance.editCell(nextRowIdx, editableFields[nextFieldIdx]);
    focusCell(instance, nextRowIdx);
  };

  const onEditorPrepared = (e) => {
    if (e.parentType === 'dataRow' && e.editorElement) {
      e.editorElement.addEventListener('keydown', (evt) => {
        if (evt.key === 'ArrowUp' || evt.key === 'ArrowDown') {
          evt.preventDefault();
          evt.stopImmediatePropagation();
        }
      }, true);
    }
  };

  const onContentReady = (e) => {
    const grid = e.element;
    if (!grid) return;
    grid.querySelectorAll('.dx-header-row td').forEach((cell) => {
      cell.setAttribute('tabindex', '-1');
    });
    grid.querySelectorAll('tr.dx-data-row button, tr.dx-data-row a').forEach((el) => {
      el.setAttribute('tabindex', '-1');
    });
    grid.querySelectorAll('.dx-datagrid-rowsview, .dx-scrollable, .dx-scrollable-container').forEach((el) => {
      el.setAttribute('tabindex', '-1');
    });
  };

  return (
    <div style={{ width: '100%' }} onBlur={onGridBlur} tabIndex="0">
      <style>{`
        .dx-revert-button { display: none !important; }
        .negative-cell {
          color: red !important;
          font-weight: bold !important;
          border: 1px solid red !important;
        }
      `}</style>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: '10px' }}>
        Define individual pieces by editing the grid below. A new row will be added automatically.
      </Typography>

      <DataGrid
        ref={dataGridRef}
        dataSource={displayList}
        repaintChangesOnly={true}
        showBorders={true}
        allowColumnReordering={true}
        allowColumnResizing={true}
        keyExpr="id"
        rowAlternationEnabled={true}
        onRowUpdated={onRowUpdated}
        onCellPrepared={onCellPrepared}
        onContentReady={onContentReady}
        onFocusedCellChanging={onFocusedCellChanging}
        onEditorPrepared={onEditorPrepared}
        onKeyDown={handleGridKeyDown}
        keyboardNavigation={{ enabled: true, editOnKeyPress: true }}
      >
        <Editing
            mode="cell"
            allowUpdating={true}
        />
        <Sorting mode="none" />
        <Column dataField="id" caption="ID" alignment="center" width={100} visible={false} allowEditing={false}/>
        <Column dataField="quantity" caption="Quantity" dataType="number" width={120}>
          <RangeRule min={0} message="Quantity cannot be negative." />
        </Column>
        <Column dataField="length" caption={isMetric ? 'Length (cm)' : 'Length (in)'} dataType="number" alignment="center" width={120}>
          <RangeRule min={0} message="Length cannot be negative." />
        </Column>
        <Column dataField="width" caption={isMetric ? 'Width (cm)' : 'Width (in)'} dataType="number" alignment="center" width={120}>
          <RangeRule min={0} message="Width cannot be negative." />
        </Column>
        <Column dataField="height" caption={isMetric ? 'Height (cm)' : 'Height (in)'} dataType="number" alignment="center" width={120}>
          <RangeRule min={0} message="Height cannot be negative." />
        </Column>
        <Column dataField="weight" caption={isMetric ? 'Weight (kg)' : 'Weight (lbs)'} dataType="number" alignment="center" width={120}>
          <RangeRule min={0} message="Weight cannot be negative." />
        </Column>
        <Column dataField="description" caption="Description" width={200}/>
        <Column dataField="positions" caption="Skid Positions" dataType="number" width={150}>
          <RangeRule min={0} message="Skid Positions cannot be negative." />
        </Column>
        <Selection mode="single"/>
        <Column caption="Delete" width={100} alignment="center" cellRender={renderDelete} allowEditing={false}/>
      </DataGrid>
    </div>
  );
}

export default DefineIndividualPieces;
